/* istanbul ignore file */

import { breakpoint } from "@porsche-design-system/components-js/styles";
import type { LatLong } from "../entities/places";
import {
  AnimationDurations,
  COOKIE_DOMAIN,
  MS_IN_MONTH,
} from "../utility/constants";
import {
  type NaviErrorType,
  NaviError,
  NaviErrorTypes,
} from "../utility/navi-error";
import { getHTMLElement, getString, hasKey, isString } from "../utility/helper";
import {
  type ConsentService,
  allConsentsAcceptedState,
  consentServiceMap,
  navStateStore,
} from "../state/nav-state-store";
import { RoutingKeyPointers } from "../entities/routing-key";

type CheckConsent = (args: string) => { consentStatus: boolean };
export const NAVIGATION_NAMESPACE = "__NAVI__";

export type MarketingWindow = Window & {
  GlobalConsent?: {
    Processor?: { googleMaps?: string; newRelic?: string };
  };
  usercentrics?: { getConsents: CheckConsent };
};

type NavigationWindow = Window & {
  PHN_AUTH_STATE?: string;
  PHN_HEADER_VERSION?: string;
  PHN_DRAWER_VERSION?: string;
  PHN_NAVIGATION_LOADED?: boolean;
  REDIRECTED?: boolean;
  [NAVIGATION_NAMESPACE]?: {
    getAuthState: () => string | undefined;
  };
};

// Requires sanity checks for SSR builds
if (typeof window !== "undefined") {
  const navWindow = window as NavigationWindow;
  navWindow[NAVIGATION_NAMESPACE] = {
    getAuthState: () => navWindow.PHN_AUTH_STATE,
  };
}

class WindowService {
  set version(version: string) {
    Object.assign(window, { PHN_HEADER_VERSION: version });
  }

  get version(): string {
    return (window as any).PHN_HEADER_VERSION;
  }

  get location(): Location {
    return window.location;
  }

  set drawerVersion(version: string) {
    Object.assign(window, { PHN_DRAWER_VERSION: version });
  }

  set authenticationState(state: string) {
    Object.assign(window, { PHN_AUTH_STATE: state });
  }

  get drawerVersion(): string {
    return (window as any).PHN_DRAWER_VERSION;
  }

  set navigationLoaded(loaded: boolean) {
    Object.assign(window, { PHN_NAVIGATION_LOADED: loaded });
  }

  getBreakpoint(): number {
    if (typeof window === "undefined") {
      return 0;
    }

    const clientWidth = Math.max(
      window.document.documentElement.clientWidth || 0,
      window.innerWidth || 0,
    );

    const matchingBreakpoint = Object.keys(breakpoint)
      .reverse()
      .find(
        (foundBreakpoint) =>
          hasKey(breakpoint, foundBreakpoint) &&
          clientWidth >= breakpoint[foundBreakpoint],
      );

    return typeof matchingBreakpoint === "string" &&
      hasKey(breakpoint, matchingBreakpoint)
      ? breakpoint[matchingBreakpoint]
      : 0;
  }

  get redirected(): boolean {
    return (
      (window as Window & { REDIRECTED?: boolean }).REDIRECTED === true ||
      window.location.search.includes("cs_redirect=")
    );
  }

  redirectToUrl(url: string): void {
    window.location.assign(url);
  }

  cookieConsentGiven(): { [key in ConsentService]: boolean } {
    const marketingWindow = window as MarketingWindow;
    const consents: { [key in ConsentService]: boolean } = {
      googleMaps: false,
      newRelic: false,
    };

    const services: ConsentService[] = Object.keys(
      consentServiceMap,
    ) as ConsentService[];

    services.forEach((service) => {
      const response = marketingWindow?.usercentrics?.getConsents(
        marketingWindow.GlobalConsent?.Processor?.[service] ??
          consentServiceMap[service],
      );
      if (response?.consentStatus) {
        consents[service] = response.consentStatus;
      }
    });

    return consents;
  }

  /**
   * GlobalConsent available + consent NOT active -> userConsent = true
   * GlobalConsent available + consent active & needed + consent given -> userConsent = true
   * GlobalConsent available + consent active & needed + consent NOT given -> userConsent = false
   * GlobalConsent available + consent active & needed + consent given LATER -> userConsent = true
   * GlobalConsent NOT available -> userConsent = true
   */
  maybeCheckMarketingConsent(): void {
    if (
      typeof (window as MarketingWindow)?.usercentrics?.getConsents ===
      "function"
    ) {
      navStateStore.state.userConsent = this.cookieConsentGiven();

      window.addEventListener("consents_changed_finished", () => {
        navStateStore.state.userConsent = this.cookieConsentGiven();
      });
    } else {
      navStateStore.state.userConsent = allConsentsAcceptedState;
    }
  }

  getScrollbarWidth(): string {
    const scrollDiv = window.document.createElement("div");
    scrollDiv.style.overflow = "scroll";
    window.document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    window.document.body.removeChild(scrollDiv);

    return Number.isNaN(scrollbarWidth) ? "0px" : `${scrollbarWidth}px`;
  }

  async fetchGoogleMapsApi(
    apiKey: string,
    url = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places`,
    // @ts-ignore
  ): Promise<typeof google> {
    // @ts-ignore
    if (window.google?.maps?.version !== undefined) {
      // @ts-ignore
      return window.google;
    }

    const existingScript = document.getElementById("googleMaps"); // -> Current name?!

    if (existingScript) {
      // @ts-ignore
      return window.google;
    }

    const script = document.createElement("script");
    script.src = url;
    script.id = "googleMaps";
    document.head.appendChild(script);

    return new Promise((resolve, reject) => {
      script.onerror = () => {
        reject(
          new NaviError(
            "Failed to load google maps script.",
            NaviErrorTypes.GENERAL,
          ),
        );
      };
      script.onload = () => {
        // @ts-ignore
        resolve(window.google);
      };
    });
  }

  async getNavigatorLatLong(): Promise<LatLong> {
    return new Promise((resolve, reject) => {
      window.navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => {
          resolve({ latitude, longitude });
        },
        (error) =>
          reject(
            new NaviError(
              `geolocation.getCurrentPosition failed with ${error.code}: ${error.message}`,
              getErrorType(error),
            ),
          ),
      );
    });
  }

  getCookie(cookieName: string): string {
    return getString(
      document.cookie
        .split("; ")
        .find((row) => row.startsWith(`${cookieName}=`))
        ?.split("=")[1],
    );
  }

  setCookie(cookieName: string, newValue: string): void {
    document.cookie = `${cookieName}=${newValue};domain=${COOKIE_DOMAIN};expires=${new Date(
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      Date.now() + MS_IN_MONTH * 10,
    ).toUTCString()};`;
  }

  private initialDocumentScrollTop = 0;

  private styleElement: HTMLStyleElement | null = null;

  private lockScrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private unlockScrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

  lockScroll(): void {
    this.lockScrollTimeoutId = setTimeout(
      () => {
        this.initialDocumentScrollTop = document.documentElement.scrollTop;

        this.styleElement = document.createElement("style");
        this.styleElement.textContent = `
    ${
      this.hasScrollbar()
        ? `html {
      scrollbar-gutter: stable;
      @media (min-width: 760px) {
        background-color: #2c2c2c;
        @media (prefers-color-scheme: light) {
          background-color: #fcfcfc;
        }
      }
    }`
        : ``
    }
    :where(body) {
      background-color: #fff;
      overflow-y: clip;
    }`;
        document.head.appendChild(this.styleElement);
      },
      AnimationDurations.drawerOpen + 5 * 20, // needs some more time in order to let the JS + animation finish. it is also not too time critical.
    );

    this.inertFooter();
  }

  unlockScroll(): void {
    this.styleElement?.remove();
    this.clearScrollTimers();
    // Eagerly start the import while the environment is still active,
    // so the setTimeout callback doesn't trigger a new import() after Jest teardown
    const routerStorePromise = import("../state/router-store").catch(
      () => null,
    );
    this.unlockScrollTimeoutId = setTimeout(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async () => {
        // this is needed in case the menu is closed before the setTimeout function in lockScroll is executed, so we need to ensure that the style element is removed in that case as well
        try {
          const mod = await routerStorePromise;
          if (!mod) return;
          const { routerStore } = mod;
          const closed =
            routerStore.state.routingKeyPointer ===
              RoutingKeyPointers.LEVEL_ZERO.valueOf() &&
            routerStore.state.animatingPointer ===
              RoutingKeyPointers.LEVEL_ZERO.valueOf();
          const closing =
            routerStore.state.routingKeyPointer === -1 &&
            routerStore.state.animatingPointer >= 0;
          if (closed || closing) {
            this.styleElement?.remove();
          }
        } catch {
          // environment may be torn down
        }
      },
      AnimationDurations.drawerOpen + 5 * 20,
    );
    this.removeInertFooter();
    document.documentElement.scrollTop = this.initialDocumentScrollTop;
  }

  clearScrollTimers(): void {
    if (this.lockScrollTimeoutId !== null) {
      clearTimeout(this.lockScrollTimeoutId);
      this.lockScrollTimeoutId = null;
    }
    if (this.unlockScrollTimeoutId !== null) {
      clearTimeout(this.unlockScrollTimeoutId);
      this.unlockScrollTimeoutId = null;
    }
  }

  removeInertFooter(): void {
    try {
      document.querySelector("pnav-footer")?.removeAttribute("inert");
    } catch (ignore) {
      /* browsers may not support inert yet or pages do not have a pnav footer */
    }
  }

  inertFooter(): void {
    try {
      document.querySelector("pnav-footer")?.setAttribute("inert", "");
    } catch (ignore) {
      /* browsers may not support inert yet or pages do not have a pnav footer */
    }
  }

  hasScrollbar(): boolean {
    return window.document.body.offsetHeight > window.innerHeight;
  }

  async waitForElement(
    parent: HTMLElement,
    selector?: string,
  ): Promise<HTMLElement> {
    if (!isString(selector)) {
      return parent;
    }

    const el = getHTMLElement(parent.querySelector(selector));
    if (el !== null) {
      return el;
    }
    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        const newEl = getHTMLElement(parent.querySelector(selector));
        if (newEl !== null) {
          resolve(newEl);
          observer.disconnect();
        }
      });

      observer.observe(parent, { childList: true, subtree: true });
    });
  }
}

interface GeolocationPositionError {
  code: number;
  message: string;
  PERMISSION_DENIED: number;
  POSITION_UNAVAILABLE: number;
  TIMEOUT: number;
}

export function getErrorType(error: GeolocationPositionError): NaviErrorType {
  switch (error.code) {
    case error.PERMISSION_DENIED: {
      return NaviErrorTypes.GEOLOCATION_DENIED;
    }
    case error.POSITION_UNAVAILABLE:
    case error.TIMEOUT: {
      return NaviErrorTypes.RETRY;
    }
    default: {
      return NaviErrorTypes.GENERAL;
    }
  }
}

export default new WindowService();
